// Money helpers for the pricing screen. The engine stores an integer number of
// CENTS; Nipa types DOLLARS AND CENTS. These conversions are the highest-risk
// part of the screen, so they live here isolated and unit-tested. We do NOT use
// `parseFloat(x) * 100` (it mis-rounds, e.g. 46964.10 → 4696409.999…); parsing
// is done from the string with integer maths.
//
// Note: the app-wide `centsToAud` (lib/format.ts) rounds to whole dollars, so it
// is unsuitable here — this screen must show the cents Nipa typed.

const AUD_EXACT = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Parse a dollars-and-cents string to an integer number of cents, WITHOUT
 * floating-point error. Accepts optional `$`, thousands commas and surrounding
 * whitespace. Returns `null` for empty / non-numeric / negative / more-than-two-
 * decimal-places input (so the caller can block the save).
 *
 * "46964.50" → 4696450 · "46964.1" → 4696410 · "0.01" → 1 · "1000" → 100000
 */
export function audToCents(input: string): number | null {
  const cleaned = input.trim().replace(/[$,\s]/g, '');
  if (cleaned === '') return null;
  // Digits with an optional 1–2 decimal places (or a bare ".dd"). No sign is
  // allowed, so the result can never be negative.
  if (!/^(\d+(\.\d{1,2})?|\.\d{1,2})$/.test(cleaned)) return null;
  const [whole, frac = ''] = cleaned.split('.');
  // padEnd, not padStart: ".5" means 5 tenths = 50 cents, ".05" means 5 cents.
  const cents = Number(whole || '0') * 100 + Number(frac.padEnd(2, '0'));
  return Number.isSafeInteger(cents) ? cents : null;
}

/**
 * Integer cents → a plain editable "dollars.cents" string (no symbol/commas),
 * for pre-filling the fee input. Integer maths only. 4696450 → "46964.50".
 */
export function centsToInput(cents: number): string {
  const whole = Math.trunc(cents / 100);
  const frac = cents % 100;
  return `${whole}.${String(frac).padStart(2, '0')}`;
}

/**
 * Integer cents → a display AUD string with thousands separators and exactly two
 * decimals. 4696450 → "$46,964.50". Null/undefined → "—".
 */
export function centsToAudDisplay(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return AUD_EXACT.format(cents / 100);
}
